import { ImageCropModal, ProfileImageUpload } from "@ews-admin/global-design-system";
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

import { storage } from "../../../firebase";
import { fetchFacilityRequest } from "../../../store/reducers/facilitySlice";
import { envConfig } from "../../../utils/config-utils";
import { updateHealthcareFacility } from "../../../webclients/facility.client";

const DEFAULT_FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1587351021759-3e566b6af7cc";

interface FacilityImageUploadProps {
  facilityId: string | number;
  currentImageUrl: string | undefined;
  imageType: "thumbnail" | "banner";
  aspectRatio: "1:1" | "3:1";
}

export const FacilityImageUpload: React.FC<FacilityImageUploadProps> = ({
  facilityId,
  currentImageUrl,
  imageType,
  aspectRatio,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [pendingFileName, setPendingFileName] = useState<string>("");

  const env = envConfig.env;
  const facilityImagesPath = `medecine360/${env}/facilityImages/`;
  const numericAspectRatio = aspectRatio === "1:1" ? 1 : 3;

  const isDefault = !currentImageUrl || currentImageUrl === "";

  const uploadFile = useCallback(
    async (file: File) => {
      if (file.size > 3 * 1024 * 1024) {
        toast.warning(t("facilityMedia.sizeExceeded", { max: 3 }));
        return;
      }

      const fileExtension = file.name.substring(file.name.lastIndexOf(".")) || ".jpg";
      const fileName = `${facilityId}_${imageType}${fileExtension}`;
      const fileWithoutExtension = `${facilityId}_${imageType}`;

      setIsUploading(true);

      try {
        const listResult = await listAll(ref(storage, facilityImagesPath));
        const existing = listResult.items.find((item) =>
          item.name.startsWith(fileWithoutExtension),
        );
        if (existing) await deleteObject(existing);

        const storageRef = ref(storage, `${facilityImagesPath}${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            setUploadProgress(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            );
          },
          (error) => {
            console.error("Upload error:", error);
            toast.error(t("facilityMedia.uploadError"));
            setIsUploading(false);
            setUploadProgress(0);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const updateData =
              imageType === "thumbnail"
                ? { thumbnailPhoto: downloadURL }
                : { bannerPhoto: downloadURL };
            await updateHealthcareFacility(updateData, Number(facilityId));
            dispatch(
              fetchFacilityRequest({ facilityId: facilityId.toString() }),
            );
            toast.success(t("facilityMedia.uploadSuccess"));
            setIsUploading(false);
          },
        );
      } catch (error) {
        console.error("Upload/delete error:", error);
        toast.error(t("facilityMedia.uploadError"));
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [facilityId, imageType, facilityImagesPath, dispatch, t],
  );

  const handleFileSelect = useCallback((file: File) => {
    const objectUrl = URL.createObjectURL(file);
    setPendingFileName(file.name);
    setCropSrc(objectUrl);
  }, []);

  const handleCropComplete = useCallback(
    (croppedFile: File) => {
      setCropSrc(null);
      uploadFile(croppedFile);
    },
    [uploadFile],
  );

  const handleCropClose = useCallback(() => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  }, [cropSrc]);

  const handleFileSizeExceeded = useCallback(() => {
    toast.warning(t("facilityMedia.sizeExceeded", { max: 3 }));
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!currentImageUrl) return;

    const fileWithoutExtension = `${facilityId}_${imageType}`;

    try {
      const listResult = await listAll(ref(storage, facilityImagesPath));
      const existing = listResult.items.find((item) =>
        item.name.startsWith(fileWithoutExtension),
      );
      if (existing) await deleteObject(existing);
    } catch (error) {
      console.error("Error deleting image:", error);
    }

    const updateData =
      imageType === "thumbnail"
        ? { thumbnailPhoto: "" }
        : { bannerPhoto: "" };
    await updateHealthcareFacility(updateData, Number(facilityId));
    dispatch(fetchFacilityRequest({ facilityId: facilityId.toString() }));
  }, [currentImageUrl, facilityId, imageType, facilityImagesPath, dispatch]);

  return (
    <>
      <ProfileImageUpload
        imageUrl={currentImageUrl || DEFAULT_FALLBACK_IMAGE}
        altText={
          imageType === "thumbnail"
            ? t("facilityMedia.thumbnail.title")
            : t("facilityMedia.banner.title")
        }
        isLoading={isUploading}
        uploadProgress={uploadProgress}
        showDeleteButton={!isDefault}
        onFileSelect={handleFileSelect}
        onFileSizeExceeded={handleFileSizeExceeded}
        onDeleteConfirm={handleDeleteConfirm}
        deleteConfirmTitle={t(`facilityMedia.${imageType}.title`)}
        deleteConfirmMessage={t(`facilityMedia.${imageType}.description`)}
        deleteConfirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
      />

      {cropSrc && (
        <ImageCropModal
          isOpen
          imageSrc={cropSrc}
          originalFileName={pendingFileName}
          aspectRatio={numericAspectRatio}
          onClose={handleCropClose}
          onCropComplete={handleCropComplete}
          title={t("facilityMedia.cropModal.title")}
          confirmLabel={t("facilityMedia.cropModal.confirm")}
          cancelLabel={t("common.cancel")}
          zoomLabel={t("facilityMedia.cropModal.zoom")}
        />
      )}
    </>
  );
};
